import React, { useEffect, useState } from "react";
import { getAuthHeader } from "../../api/authToken.js";
import { useNavigate, Link } from "react-router-dom";
import { exportToExcel } from "../../components/ExportToExcel.js";
import "../../styles/App.css";
import GroupLabel, { NO_GROUP_TEXT } from "../../components/GroupLabel.js";

const RatingPage = () => {
  const [students, setStudents] = useState([]);
  const [dormitories, setDormitories] = useState([]);
  const [selectedDormitory, setSelectedDormitory] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "rating",
    direction: "desc",
  });
  const API = process.env.REACT_APP_API_BASE;

  const navigate = useNavigate();

  useEffect(() => {
    fetchDormitories();
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedDormitory) {
      fetchStudentsByDormitory(selectedDormitory);
    } else {
      fetchStudents();
    }
  }, [selectedDormitory]);

  const fetchDormitories = async () => {
    try {
      const response = await fetch(`${API}/api/dormitories`, {
        headers: { Authorization: getAuthHeader() },
      });
      if (response.ok) {
        const data = await response.json();
        setDormitories(data);
      } else {
        alert("Не вдалося завантажити список гуртожитків.");
      }
    } catch (error) {
      console.error("Помилка завантаження гуртожитків:", error);
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await fetch(`${API}/api/rating/`, {
        headers: { Authorization: getAuthHeader() },
      });
      if (response.ok) {
        const data = await response.json();
        setStudents(sortData(data, sortConfig));
      } else {
        alert("Не вдалося завантажити список студентів.");
      }
    } catch (error) {
      console.error("Помилка завантаження студентів:", error);
    }
  };

  const fetchStudentsByDormitory = async (dormitoryId) => {
    try {
      const response = await fetch(
        `${API}/api/students-by-dormitory?dormitory_id=${dormitoryId}`,
        {
          headers: { Authorization: getAuthHeader() },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setStudents(sortData(data, sortConfig));
      } else {
        alert("Не вдалося завантажити студентів за гуртожитком.");
      }
    } catch (error) {
      console.error("Помилка завантаження студентів за гуртожитком:", error);
    }
  };

  const sortData = (data, config) => {
    const sortedData = [...data].sort((a, b) => {
      if (a[config.key] < b[config.key]) {
        return config.direction === "asc" ? -1 : 1;
      }
      if (a[config.key] > b[config.key]) {
        return config.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
    return sortedData;
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    setStudents((prevStudents) => sortData(prevStudents, { key, direction }));
  };

  // Обробка кліку на рядок
  const handleRowClick = (studentId) => {
    navigate(`/student/${studentId}`); // Перехід на сторінку студента
  };

  const handleExport = () => {
    exportToExcel(
      students,
      "Рейтинг студентів",
      "Рейтинг_студентів.xlsx",
      (student) => ({
        ПІБ: student.pib,
        Група: student.group || NO_GROUP_TEXT,
        Рейтинг: student.rating,
        Кімната: student.room_number || "Не вказано",
      })
    );
  };
  
  return (
    <div className="page-container">
      <div className="header-content-title">
        Виберіть гуртожиток:
        <select
          className="header-content-select"
          value={selectedDormitory}
          onChange={(e) => setSelectedDormitory(e.target.value)}
        >
          <option value="">Усі гуртожитки</option>
          {dormitories.map((dormitory) => (
            <option key={dormitory.id} value={dormitory.id}>
              {dormitory.name}
            </option>
          ))}
        </select>
      </div>

      <div className="header-content">
        <Link to="/rating-add" className="propese-button">
          Додати рейтинг студенту
        </Link>
        <a href="#" onClick={handleExport} className="propese-button">
          Експортувати в Excel
        </a>
      </div>
      <div className="header-content-title">
        <label>
          100 балів - всього <br />
          50 балів - середній бал за навчання (шкала 30–50; нижче 30 — без сесії)
          <br />
          25 балів - комендант гуртожика
          <br />
          25 балів - від зам. декана <br />
        </label>
      </div>

      <table className="table">
        <thead>
          <tr style={{ cursor: "pointer" }}>
            <th className="table-header" onClick={() => handleSort("pib")}>
              ПІБ{" "}
              {sortConfig.key === "pib"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
            <th className="table-header" onClick={() => handleSort("group")}>
              Група{" "}
              {sortConfig.key === "group"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
            <th className="table-header" onClick={() => handleSort("rating")}>
              Рейтинг{" "}
              {sortConfig.key === "rating"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
            <th
              className="table-header"
              onClick={() => handleSort("acts_count")}
            >
              Кіл. актів{" "}
              {sortConfig.key === "acts_count"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
            <th
              className="table-header"
              onClick={() => handleSort("room_number")}
            >
              Кімната{" "}
              {sortConfig.key === "room_number"
                ? sortConfig.direction === "asc"
                  ? "↑"
                  : "↓"
                : "↑↓"}
            </th>
          </tr>
        </thead>
        <tbody>
          {students.map((student) => (
            <tr
              key={student.id}
              onClick={() => handleRowClick(student.id)}
              style={{ cursor: "pointer" }}
            >
              <td className="table-content">{student.pib}</td>
              <td className="table-content"><GroupLabel group={student.group} /></td>
              <td className="table-content">{student.rating}</td>
              <td className="table-content">{student.acts_count}</td>
              <td className="table-content">
                {student.dormitory_name} - {student.room_number || "Не вказано"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RatingPage;
